import { NetworkStatus } from "@apollo/client";
import { AIAssessmentsList, AssessmentGeneralConditionCard, Typography } from "@cpj-ai/panel-npm-package";
import { useGetEmployeeJobAssessmentsQuery } from "@graphql/generated";
import { Spinner, useDisclosure } from "@heroui/react";
import { useState } from "react";
import AssessmentDetailModal from "./AssessmentDetailModal";

interface AssessmentTabContentProps {
  employeeId?: string;
}

type AssessmentDetail = {
  id: string;
  title: string;
  description: string;
  status: string;
  remainingQuestions: number;
  totalEstimatedTime: number;
  totalQuestions: number;
  skill?: {
    title: string;
    description: string;
  } | null;
  results?: Array<{
    evaluation?: {
      score: string;
      userFeedback: string;
      negativePoints: Array<string | null>;
      positivePoints: Array<string | null>;
      scoreReasoning: string;
      scoreValue?: unknown;
    } | null;
    question: {
      text: string;
    };
  } | null> | null;
} | null;

export default function AssessmentTabContent({ employeeId }: AssessmentTabContentProps) {
  const { isOpen, onOpen, onClose, onOpenChange } = useDisclosure();
  const [selectedAssessment, setSelectedAssessment] = useState<AssessmentDetail>(null);

  const {
    data,
    loading: assessmentsLoading,
    error: assessmentsError,
    refetch,
    networkStatus,
  } = useGetEmployeeJobAssessmentsQuery({
    variables: { id: employeeId ?? "" },
  });
  const assessments = data?.organization?.employee?.get?.employee?.assessments;

  if (assessmentsLoading) {
    return (
      <div className="flex justify-center items-center h-full">
        <Spinner />
      </div>
    );
  }

  if (!assessments?.length) {
    return (
      <div className="flex justify-center items-center h-full">
        <Typography className="text-center mt-20">There are no assessments</Typography>
      </div>
    );
  }

  return (
    <div>
      <AssessmentGeneralConditionCard
        type={"mandatory"}
        assessmentsData={{ me: { assessments } }}
        assessmentsLoading={networkStatus === NetworkStatus.refetch || assessmentsLoading}
      />
      <AIAssessmentsList
        assessmentsData={{ me: { assessments } }}
        assessmentsLoading={assessmentsLoading}
        assessmentsError={assessmentsError}
        onAssessmentEnd={refetch}
        isClickableCard={false}
        handleOpenAssessmentDetailModalInOrg={(data) => {
          const assessmentDetail = assessments?.find((assessment) => assessment?.id === data.id);
          if (assessmentDetail) {
            setSelectedAssessment(assessmentDetail);
            onOpen();
          }
        }}
      />

      <AssessmentDetailModal
        isOpen={isOpen}
        onClose={onClose}
        onOpenChange={onOpenChange}
        assessmentDetail={selectedAssessment}
      />
    </div>
  );
}
